import React, { useRef } from "react";
import { observer } from "mobx-react";
import { default as MaterialTable, TableFilterList } from "mui-datatables";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { formatAMPM } from "../../helpers";
import {
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  Typography,
  Chip
} from '@material-ui/core';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import '../../Styling/laundromat.css';
import store from '../Stores/SurveyStore';
import { PabsCleaners } from '../../Enums/constants';

const getCleanersList = () => Object.values(PabsCleaners).map(c => c.name);

const LaundromatTable = observer((props) => {
  const {
    surveyType = "PABS",
    data = [],
    onStatusChange,
    onCleanerChange,
    onQuestionsSentDateChange,
    loading = false
  } = props;


  const CustomChip = (props) => {
    let value = props.label;

    return (
      <Chip
        variant="outlined"
        color="primary"
        label={value}
        onDelete={props.onDelete}
      />
    );
  };

  const CustomFilterList = (props) => {
    return <div style={{ padding: "10px" }}><TableFilterList {...props} ItemComponent={CustomChip} /></div>;
  };


  const formSurveyCleaners = (survey) => {
    let cleaners = [];

    // White space in tag does not get saved to the database
    cleaners.push(<MenuItem value={null} >&nbsp;</MenuItem>);

    // localeCompare used because it returns negative and positive numbers as the result of comparison, which is needed for sort
    let sortedCleaners = [...store.surveyCleanersPABS].sort((a, b) => (a.firstName ?? "").toLowerCase().localeCompare(b.firstName?.toLowerCase() ?? ""));
    for (let cleaner of sortedCleaners) {
      cleaners.push(<MenuItem value={(cleaner.id ?? "")} >{(cleaner.firstName ?? "") + "  " + (cleaner.lastName ?? "")}</MenuItem>);
    }

    return cleaners;
  }



  const beam = async (surveyId) => {
    const survey = data.find(s => s.surveyId === surveyId);
    if (!survey) {
      console.error('Survey not found:', surveyId);
      return;
    }

    // Determine the survey type URL based on survey type
    let surveyTypeUrl = '/pabs/';
    if (survey.surveyTypeDesc?.toLowerCase().includes('uk')) {
      surveyTypeUrl = '/pabsuk/';
    } else if (survey.surveyTypeDesc?.toLowerCase().includes('lg')) {
      surveyTypeUrl = '/pabslg/';
    }

    // Navigate to the survey home page
    window.location.href = surveyTypeUrl + 'home';

    // Store the survey selection in localStorage for the survey to pick up
    localStorage.setItem('selectedPabsSurveyId', surveyId);
    localStorage.setItem('selectedPabsSystemId', survey.systemId);
    localStorage.setItem('selectedPabsDataYear', survey.dataYear);
  };

  const handleStatusChange = async (surveyId, e) => {
    const statusName = e.target.value;

    // Map status name back to ID for the API
    const statusType = store.pabsSurveyStatusTypes?.find(st => st.name === statusName);
    const statusId = statusType?.id;

    if (statusId && onStatusChange) {
      await onStatusChange(surveyId, statusId);
    }
  };

  const handleCleanerChange = async (surveyId, e) => {
    if (onCleanerChange) {
      await onCleanerChange(surveyId, e.target.value);
    }
  };

  const StatusDataColumn = observer((props) => {
    const survey = data.find(s => s.surveyId == props.surveyId);
    if (!survey) {
      return <></>;
    }

    // Use the surveyStatusName directly since it's more reliable
    const status = survey.surveyStatusName || "";
    return <>{status}</>;
  });

  const CleanerDataColumn = observer((props) => {
    const survey = data.find(s => s.surveyId == props.surveyId);
    if (!survey) {
      return <></>;
    }
    return <>{store.surveyCleanersMapPABS[survey?.surveyCleanerID]?.firstName || ""}</>;
  });

  const QuestionsDateDataColumn = observer((props) => {
    const survey = data.find(s => s.surveyId == props.surveyId);
    if (!survey) {
      return <></>;
    }

    if (survey.dateQuestionsSent) {
      let date = new Date(survey.dateQuestionsSent);
      return <>{formatAMPM(date)}</>;
    }
    return <></>;
  });

  const DateDataColumn = observer(({ surveyId, field }) => {
    const survey = data.find(s => s.surveyId == surveyId);
    if (!survey) return <></>;
    const val = survey[field];
    if (val) {
      let date = new Date(val);
      return <>{formatAMPM(date)}</>;
    }
    return <></>;
  });

  const QuestionsDateField = observer(({ surveyId, onQuestionsSentDateChange }) => {
    const survey = data.find(s => s.surveyId === surveyId);

    const dateValue = (() => {
      if (survey?.dateQuestionsSent) {
        const date = new Date(survey.dateQuestionsSent);
        return date.toISOString().slice(0, 10);
      }
      return "";
    })();

    return (
      <TextField
        label="Questions Sent"
        type="date"
        value={dateValue}
        InputLabelProps={{
          shrink: true,
        }}
        onChange={(e) => {
          if (onQuestionsSentDateChange) {
            onQuestionsSentDateChange(surveyId, e.target.value);
          }
        }}
        style={{ width: "100%" }}
      />
    );
  });

  const LaundromatTableExpandableRow = observer((props) => {
    const surveyId = props.surveyId;
    const colSpan = props.colSpan;
    const survey = data.find(s => s.surveyId === surveyId);

    const menuPropsDropdown = {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      getContentAnchorEl: null
    };


    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <Grid container spacing={2} style={{ width: "100%", padding: "10px" }}>
            <Grid item xs={12}>
              <Typography style={{ fontSize: "1rem", fontWeight: "bold" }}>
                {survey?.orgName} ({survey?.dataYear} - {survey?.surveyTypeDesc})
              </Typography>
            </Grid>

            {/* Status */}
            <Grid item lg={3} md={4} xs={6}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id="status-input-label">Status</InputLabel>
                <Select
                  labelId="status-input-label"
                  value={survey?.surveyStatusName || ""}
                  MenuProps={menuPropsDropdown}
                  onChange={(e) => handleStatusChange(surveyId, e)}
                >
                  <MenuItem value="">&nbsp;</MenuItem>
                  {store.pabsSurveyStatusTypes?.map(statusType => (
                    <MenuItem key={statusType.id} value={statusType.name}>
                      {statusType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>


            {/* Cleaner */}
            <Grid item lg={3} md={4} xs={6}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id="cleaner-input-label">Cleaner</InputLabel>
                <Select
                  labelId="cleaner-input-label"
                  value={(() => {
                    // Get cleaner ID from current cleaner name or fallback to cleanerUserId
                    return survey?.surveyCleanerID;
                  })()}
                  MenuProps={menuPropsDropdown}
                  onChange={(e) => handleCleanerChange(surveyId, e)}
                >
                  {formSurveyCleaners(survey)}
                </Select>
              </FormControl>
            </Grid>

            {/* Questions Sent Date */}
            <Grid item lg={3} md={4} xs={6}>
              <QuestionsDateField
                surveyId={surveyId}
                onQuestionsSentDateChange={props.onQuestionsSentDateChange}
              />
            </Grid>

          </Grid>
        </TableCell>
      </TableRow>
    );
  });

  const muiTheme = () => createTheme({
    overrides: {
      MUIDataTableSelectCell: {
        headerCell: {
          backgroundColor: "rgb(91, 100, 109)",
          color: "#FFFFFF"
        }
      },
      MuiButton: {
        root: {
          color: "#FFFFFF"
        }
      },
      MUIDataTableBodyRow: {
        root: {
          '&:hover': {
            cursor: "pointer"
          }
        },
      },
      MUIDataTableBodyCell: {
        root: {
          textOverflow: "ellipsis",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden"
        }
      },
      MUIDataTableHeadCell: {
        root: {
          backgroundColor: "rgb(91, 100, 109)",
          color: "#FFFFFF",
          textAlign: "left",
        },
        fixedHeader: {
          backgroundColor: "rgb(91, 100, 109)",
          color: "#FFFFFF",
          textAlign: "left",
        },
        button: {
          color: "#FFFFFF",
          textAlign: "center"
        },
        sortActive: {
          color: "#FFFFFF"
        }
      },
      MUIDataTable: {
        paper: {
          height: '100%',
        },
        responsiveScroll: {
          maxHeight: 'none',
          height: 'calc(100% - 128px)'
        }
      }
    }
  });

  //forces the filters to drop down under the selection
  //also forces a scroll window if there are many options for the filter
  const ITEM_HEIGHT = 48;//this is pixels
  const filterMenuPropsDropdown = {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left"
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left"
    },
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 5
      }
    },
    getContentAnchorEl: null
  };

  //custom dropdown menu behaviour for laudromat filters
  //slightly modified from LaundromatPage's version to account for PABS data storage
  //filterLabel: what the filter is listed as in the menu
  //filterName: the name of the column we are building a dropdown menu for
  //labelFor: optional, the text that will be displayed when you apply a filter (uses raw stored value)
  //getChoiceValue: optional, this fixes the issue where survey cleaners are stored in a different location
  //normalize: optional, this is when an option is an "absense of" a status ("No Status" for Survey Status is an example of this)
  //sort with: optional, by default is alphabetical
  const buildFilterDropdown = ({
    filterLabel,
    filterName,
    labelFor = v => v,
    getChoiceValue = row => row[filterName],
    normalize = v => (v === null || v === undefined || v === '') ? "Not Specified" : v,
    sortWith = undefined
  }) => ({
    filter: true,
    filterType: 'custom',
    filterOptions: {
      logic: (value, filters) =>
        filters.length > 0 && !filters.includes(normalize(value)),
      display: (filterList, onChange, index, column) => {
        const choices = [...new Set(
          (data ?? [])
            .map(getChoiceValue)
            .map(normalize)
            .filter(v => v !== null && v !== undefined)
        )].sort(sortWith);

        return (
          <FormControl style={{ width: '100%' }}>
            <InputLabel>{filterLabel}</InputLabel>
            <Select multiple
              value={filterList[index] || []}
              onChange={event => onChange(event.target.value, index, column)}
              MenuProps={filterMenuPropsDropdown}
              renderValue={selected => selected.map(labelFor).join(', ')}
            >
              {choices.map(choice => (
                <MenuItem key={choice} value={choice}>
                  {labelFor(choice)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
    }
  });

  const columns = [
    {
      name: "surveyId",
      options: {
        filter: false,
        display: false
      }
    },
    {
      name: "",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          let rowData = tableMeta.currentTableData[tableMeta.rowIndex].data;
          let surveyId = rowData[0];
          return (
            <div style={{ width: "100%", height: "100%", display: "inline-block" }}>
              <button
                style={{ width: "fit-content", height: "80%", fontSize: "13px" }}
                className="btn btn-add-row"
                onClick={() => beam(surveyId)}
              >
                <i className="fa fa-lg fa-sign-in-alt fa-nohover2" aria-hidden="true"></i>
              </button>
            </div>
          );
        }
      }
    },
    {
      label: "Organization",
      name: "orgName",
      options: {
        ...buildFilterDropdown({ filterLabel: 'Organization', filterName: 'orgName' })
        //setCellProps AND setHeaderProps DO NOT WORK BECAUSE OF table-layout: fixed
        , setCellProps: () => ({
          style: {
            maxWidth: 150,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        })
        , setHeaderProps: () => ({
          style: {
            maxWidth: 150
          }
        })
      }
    },
    {
      label: "Survey Type",
      name: "surveyTypeDesc",
      options: buildFilterDropdown({ filterLabel: 'Survey Type', filterName: 'surveyTypeDesc' })
    },
    {
      label: "Country",
      name: "countryName",
      options: buildFilterDropdown({ filterLabel: 'Country', filterName: 'countryName' })
    },
    {
      label: "Data Year",
      name: "dataYear",
      options: buildFilterDropdown({
        filterLabel: 'Data Year',
        filterName: 'dataYear',
        normalize: v => Number(v),
        sortWith: (a, b) => b - a
      }),
    },
    {
      label: "Questions Sent",
      name: "dateQuestionsSent",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          let rowData = tableMeta.currentTableData[tableMeta.rowIndex].data;
          let surveyId = rowData[0];
          return <QuestionsDateDataColumn surveyId={surveyId} />;
        }
      }
    },
    {
      label: "First Submitted",
      name: "dateFirstSubmitted",
      options: {
        filter: false,
        customBodyRender: value => {
          if (value) {
            let date = new Date(value);
            return <>{formatAMPM(date)}</>;
          }
          return <></>;
        }
      }
    },
    {
      label: "Submitted",
      name: "dateSubmitted",
      options: {
        filter: false,
        sort: true,
        sortDirection: 'desc',
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const a = new Date(obj1.data);
            const b = new Date(obj2.data);
            return (a - b) * (order === 'asc' ? 1 : -1);
          };
        },
        customBodyRender: value => {
          if (value) {
            let date = new Date(value);
            return <>{formatAMPM(date)}</>;
          }
          return <></>;
        }
      }
    },
    {
      label: "First Done",
      name: "dateFirstDone",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) => {
          let rowData = tableMeta.currentTableData[tableMeta.rowIndex].data;
          let surveyId = rowData[0];
          return <DateDataColumn surveyId={surveyId} field="dateFirstDone" />;
        }
      }
    },
    {
      label: "Last Active",
      name: "dateLastActive",
      options: {
        filter: false,
        customBodyRender: value => {
          if (value) {
            let date = new Date(value);
            return <>{formatAMPM(date)}</>;
          }
          return <></>;
        }
      }
    },
    {
      label: "Changes",
      name: "numChanges",
      options: {
        filter: false
      }
    },
    {
      label: "Status",
      name: "surveyStatusName",
      options: {

        ...buildFilterDropdown({
          filterLabel: 'Status',
          filterName: 'surveyStatusName',
          normalize: v => v || "No Status"
        }),
        customBodyRender: (value, tableMeta) => {
          let rowData = tableMeta.currentTableData[tableMeta.rowIndex].data;
          let surveyId = rowData[0];
          return <StatusDataColumn surveyId={surveyId} />;
        }
      }
    },
    {
      label: "Cleaner",
      name: "cleaner",
      options: {
        ...buildFilterDropdown({
          filterLabel: 'Cleaner',
          filterName: 'SurveyCleanersMixed',
          getChoiceValue: s => store.surveyCleanersMapPABS[s?.surveyCleanerID]?.firstName,
          normalize: v => v || "No Cleaner"
        }),
        customBodyRender: (value, tableMeta) => {
          let rowData = tableMeta.currentTableData[tableMeta.rowIndex].data;
          let surveyId = rowData[0];
          const survey = data.find(s => s.surveyId == surveyId);
          const name = store.surveyCleanersMapPABS[survey?.surveyCleanerID]?.firstName || "";
          return <CleanerDataColumn surveyId={surveyId} value={name} />;
        }
      }
    }
  ];

  const tableRef = useRef(null);

  if (loading || !data) {
    return (
      <Container fluid>
        <Row>
          <Col>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h4>Loading laundromat data...</h4>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (data.length === 0) {
    return (
      <Container fluid>
        <Row>
          <Col>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h4>No survey data found</h4>
              <p>There are no surveys available to display in the laundromat.</p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <ThemeProvider theme={muiTheme()}>
            <div id="laundromat-page-table">
              <MaterialTable
                title={
                  <div className="MUIDataTableToolbar-titleRoot-19" aria-hidden="true">
                    <h6 className="MuiTypography-root MUIDataTableToolbar-titleText-20 MuiTypography-h6">
                      {surveyType} Laundromat
                    </h6>
                  </div>
                }
                ref={tableRef}
                options={{
                  pagination: true,
                  rowsPerPage: 100,
                  selectableRows: "single",
                  selectableRowsHeader: false,
                  selectableRowsHideCheckboxes: true,
                  selectToolbarPlacement: "none",
                  filter: true,
                  print: false,
                  download: false,
                  viewColumns: true,
                  fixedHeader: true,
                  sortOrder: {
                    name: "dateSubmitted",
                    direction: "desc"
                  },
                  tableBodyMaxHeight: "95%",
                  textLabels: {
                    pagination: {
                      next: "Next Page",
                      previous: "Previous Page",
                      rowsPerPage: "",
                      displayRows: "of",
                    }
                  },
                  expandableRows: true,
                  expandableRowsHeader: false,
                  expandableRowsOnClick: true,
                  renderExpandableRow: (tableRowData, rowMeta) => {
                    const colSpan = tableRowData.length;
                    const surveyId = tableRowData[0];
                    return <LaundromatTableExpandableRow colSpan={colSpan} surveyId={surveyId} onQuestionsSentDateChange={onQuestionsSentDateChange} />;
                  }
                }}
                columns={columns}
                data={data && data.length > 0 ? data : []}
                components={{
                  TableFilterList: CustomFilterList,
                }}
              />
            </div>
          </ThemeProvider>
        </Col>
      </Row>
    </Container>
  );
});

export default LaundromatTable;